import { Code, Database, Rocket } from "lucide-react";
import AnimatedText from "./AnimatedText";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section
      id="about"
      className="py-20 md:py-32 bg-black overflow-hidden"
      ref={sectionRef}
    >
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div
            className={cn(
              "lg:col-span-5 transition-all duration-1000",
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-20"
            )}
          >
            {/* Profile image */}
            <div
              className="mb-8 w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden border-2 border-white/20 shadow-lg shadow-white/5 mx-auto lg:mx-0"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0) scale(1)" : "translateY(20px) scale(0.95)",
                transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
              }}
            >
              <img
                src="/gaurav-gadhari-about.png"
                alt="Gaurav Gadhari - Full Stack Developer and AI Builder"
                title="Gaurav Gadhari"
                width={224}
                height={224}
                loading="lazy"
                className="w-full h-full object-cover object-top"
                itemProp="image"
              />
            </div>
            <h2 className="font-display text-5xl md:text-6xl tracking-tight mb-8">
              <AnimatedText text="THE PRODIGY" />
            </h2>
            <div className="space-y-6 text-base">
              <p
                className="transform transition-all duration-500 delay-200"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? "translateY(0)" : "translateY(20px)",
                }}
              >
                Gaurav Gadhari began coding in the 7th grade, when most were
                still figuring out middle school dynamics. Inspired by the
                inventive minds of Senku and Gen Asagiri, he approached
                programming not as a hobby, but as a means to reshape reality.
              </p>
              <p
                className="transform transition-all duration-500 delay-400"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? "translateY(0)" : "translateY(20px)",
                }}
              >
                By his mid-teens, he was already architecting full-stack
                applications and diving into the complexities of artificial
                intelligence. His approach combines technical precision with
                ambitious vision — a rare fusion that produces innovations that
                seem to arrive from the future.
              </p>
            </div>
          </div>

          <div className="lg:col-span-1"></div>

          <div
            className={cn(
              "lg:col-span-6 transition-all duration-1000",
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-20"
            )}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Feature 1 */}
              <div
                className="border border-white p-6 hover:bg-white hover:text-black transition-all duration-500 group relative overflow-hidden transform opacity-0 animate-fade-in"
                style={{ animationDelay: "300ms" }}
              >
                <div className="relative z-10">
                  <Code className="mb-4 h-8 w-8 group-hover:text-black transition-colors duration-300" />
                  <h3 className="font-display text-xl mb-3">PROGRAMMER</h3>
                  <p className="text-sm">
                    Fluent in multiple programming languages and paradigms,
                    crafting elegant solutions to complex problems.
                  </p>
                </div>

                {/* Background animation */}
                <div className="absolute inset-0 bg-white transform scale-y-0 origin-bottom group-hover:scale-y-100 transition-transform duration-500"></div>

                {/* Corners */}
                <div className="absolute top-0 left-0 w-4 h-4 border-t border-l border-white scale-0 group-hover:scale-100 transition-transform duration-300 origin-top-left z-20"></div>
                <div className="absolute top-0 right-0 w-4 h-4 border-t border-r border-white scale-0 group-hover:scale-100 transition-transform duration-300 origin-top-right z-20"></div>
                <div className="absolute bottom-0 left-0 w-4 h-4 border-b border-l border-white scale-0 group-hover:scale-100 transition-transform duration-300 origin-bottom-left z-20"></div>
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b border-r border-white scale-0 group-hover:scale-100 transition-transform duration-300 origin-bottom-right z-20"></div>
              </div>

              {/* Feature 2 */}
              <div
                className="border border-white p-6 hover:bg-white hover:text-black transition-all duration-500 group relative overflow-hidden transform opacity-0 animate-fade-in"
                style={{ animationDelay: "400ms" }}
              >
                <div className="relative z-10">
                  <Database className="mb-4 h-8 w-8 group-hover:text-black transition-colors duration-300" />
                  <h3 className="font-display text-xl mb-3">AI BUILDER</h3>
                  <p className="text-sm">
                    Developing AI systems that push boundaries and unlock new
                    possibilities in how we interact with technology.
                  </p>
                </div>

                {/* Background animation */}
                <div className="absolute inset-0 bg-white transform scale-y-0 origin-bottom group-hover:scale-y-100 transition-transform duration-500"></div>

                {/* Corners */}
                <div className="absolute top-0 left-0 w-4 h-4 border-t border-l border-white scale-0 group-hover:scale-100 transition-transform duration-300 origin-top-left z-20"></div>
                <div className="absolute top-0 right-0 w-4 h-4 border-t border-r border-white scale-0 group-hover:scale-100 transition-transform duration-300 origin-top-right z-20"></div>
                <div className="absolute bottom-0 left-0 w-4 h-4 border-b border-l border-white scale-0 group-hover:scale-100 transition-transform duration-300 origin-bottom-left z-20"></div>
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b border-r border-white scale-0 group-hover:scale-100 transition-transform duration-300 origin-bottom-right z-20"></div>
              </div>

              {/* Feature 3 */}
              <div
                className="border border-white p-6 hover:bg-white hover:text-black transition-all duration-500 group relative overflow-hidden transform opacity-0 animate-fade-in"
                style={{ animationDelay: "500ms" }}
              >
                <div className="relative z-10">
                  <Rocket className="mb-4 h-8 w-8 group-hover:text-black transition-colors duration-300" />
                  <h3 className="font-display text-xl mb-3">FOUNDER</h3>
                  <p className="text-sm">
                    Creating and launching ventures that transform ideas into
                    impactful products and services.
                  </p>
                </div>

                {/* Background animation */}
                <div className="absolute inset-0 bg-white transform scale-y-0 origin-bottom group-hover:scale-y-100 transition-transform duration-500"></div>

                {/* Corners */}
                <div className="absolute top-0 left-0 w-4 h-4 border-t border-l border-white scale-0 group-hover:scale-100 transition-transform duration-300 origin-top-left z-20"></div>
                <div className="absolute top-0 right-0 w-4 h-4 border-t border-r border-white scale-0 group-hover:scale-100 transition-transform duration-300 origin-top-right z-20"></div>
                <div className="absolute bottom-0 left-0 w-4 h-4 border-b border-l border-white scale-0 group-hover:scale-100 transition-transform duration-300 origin-bottom-left z-20"></div>
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b border-r border-white scale-0 group-hover:scale-100 transition-transform duration-300 origin-bottom-right z-20"></div>
              </div>

              {/* Quote with glitch effect */}
              <div
                className="border border-white p-6 overflow-hidden relative transform opacity-0 animate-fade-in"
                style={{ animationDelay: "600ms" }}
              >
                <div className="relative z-10">
                  <p
                    className="text-sm italic glitch-text"
                    data-text="In the realm where logic meets creativity, that's where the real magic happens."
                  >
                    "In the realm where logic meets creativity, that's where the
                    real magic happens."
                  </p>
                  <p className="text-right mt-4 text-xs">— Gaurav Gadhari</p>
                </div>

                {/* Animated glitch line */}
                <div className="absolute h-0.5 bg-white/80 w-full top-1/2 left-0 transform -translate-y-1/2 -translate-x-full glitch-line"></div>
              </div>
            </div>

            {/* Running text line with enhanced animation */}
            <div
              className="mt-8 w-full overflow-hidden py-4 border-y border-white opacity-0 animate-fade-in"
              style={{ animationDelay: "700ms" }}
            >
              <div className="marquee-container">
                <div className="marquee-content whitespace-nowrap animate-marquee">
                  <span className="font-display text-3xl md:text-4xl tracking-tight mx-4 inline-block hover:text-black hover:bg-white transition-all duration-300 px-2">
                    INNOVATOR
                  </span>
                  <span className="font-display text-3xl md:text-4xl tracking-tight mx-4 inline-block hover:text-black hover:bg-white transition-all duration-300 px-2">
                    VISIONARY
                  </span>
                  <span className="font-display text-3xl md:text-4xl tracking-tight mx-4 inline-block hover:text-black hover:bg-white transition-all duration-300 px-2">
                    CREATOR
                  </span>
                  <span className="font-display text-3xl md:text-4xl tracking-tight mx-4 inline-block hover:text-black hover:bg-white transition-all duration-300 px-2">
                    BUILDER
                  </span>
                  <span className="font-display text-3xl md:text-4xl tracking-tight mx-4 inline-block hover:text-black hover:bg-white transition-all duration-300 px-2">
                    INNOVATOR
                  </span>
                  <span className="font-display text-3xl md:text-4xl tracking-tight mx-4 inline-block hover:text-black hover:bg-white transition-all duration-300 px-2">
                    VISIONARY
                  </span>
                  <span className="font-display text-3xl md:text-4xl tracking-tight mx-4 inline-block hover:text-black hover:bg-white transition-all duration-300 px-2">
                    CREATOR
                  </span>
                  <span className="font-display text-3xl md:text-4xl tracking-tight mx-4 inline-block hover:text-black hover:bg-white transition-all duration-300 px-2">
                    BUILDER
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
